import { Anchor, Button, Modal, Stack, Text } from '@mantine/core';
import fileData from '../../../interface/props/table/files';
import {
  formatDeckAssessmentRows,
  formatDppIdentificationRows,
  formatNotesExtractionRows,
  formatScheduleSyncRows,
  formatChapterTagSyncRows,
  getOriginalNotesPdfUrl,
  getUpdatedNotesPdfUrl,
  summarizeDeckAssessment,
  summarizeDppIdentification,
} from '../../../utils/notesOcr';
import styles from './DeckAssessmentModal.module.css';

type Props = {
  opened: boolean;
  close: () => void;
  document: fileData;
};

const DeckAssessmentModal = ({ opened, close, document }: Props) => {
  const fileDetails = document.fileDetails;
  const assessment = fileDetails?.deckAssessment;
  const dppIdentification = fileDetails?.dppIdentification;

  if (!assessment && !dppIdentification) {
    return null;
  }

  const updatedPdfUrl = getUpdatedNotesPdfUrl(fileDetails);
  const originalPdfUrl = getOriginalNotesPdfUrl(fileDetails);
  const summary = assessment
    ? summarizeDeckAssessment(fileDetails, document.center)
    : null;
  const dppSummary = dppIdentification
    ? summarizeDppIdentification(dppIdentification)
    : null;
  const extractionRows = formatNotesExtractionRows(fileDetails);
  const deckRows = assessment
    ? formatDeckAssessmentRows(assessment, document.center)
    : [];
  const dppRows = formatDppIdentificationRows(dppIdentification);
  const scheduleSyncRows = formatScheduleSyncRows(fileDetails);
  const chapterTagSyncRows = formatChapterTagSyncRows(fileDetails);

  return (
    <Modal
      opened={opened}
      onClose={close}
      centered
      size="lg"
      title={`Notes pipeline — ${document.fileName}`}
    >
      <Stack spacing="md">
        {summary && summary.issues.length > 0 && (
          <Stack spacing={4}>
            <Text fw={600} size="sm">
              Deck issues detected
            </Text>
            <ul className={styles.issueList}>
              {summary.issues.map((issue) => (
                <li key={issue.id} className={styles.issueItem}>
                  <span
                    className={
                      issue.fixedInUpdatedPdf
                        ? styles.issueTick
                        : styles.issueCross
                    }
                    aria-hidden
                  >
                    {issue.fixedInUpdatedPdf ? '✓' : '✗'}
                  </span>
                  <span>{issue.message}</span>
                </li>
              ))}
            </ul>
          </Stack>
        )}

        {dppSummary && dppSummary.issueCount > 0 && (
          <Stack spacing={4}>
            <Text fw={600} size="sm">
              DPP issues detected
            </Text>
            <ul className={styles.issueList}>
              {dppSummary.issues.map((issue) => (
                <li key={issue.id} className={styles.issueItem}>
                  <span className={styles.issueCross} aria-hidden>
                    ✗
                  </span>
                  <span>{issue.message}</span>
                </li>
              ))}
            </ul>
          </Stack>
        )}

        {dppSummary && dppSummary.issueCount === 0 && (
          <Text className={styles['status-ok']}>{dppSummary.statusLabel}</Text>
        )}

        <Stack spacing={4}>
          <Text fw={600} size="sm">
            OCR extraction
          </Text>
          <div className={styles.detailGrid}>
            {extractionRows.map((row) => (
              <div key={row.label} className={styles.detailRow}>
                <span className={styles.detailLabel}>{row.label}</span>
                <span>{row.value}</span>
              </div>
            ))}
          </div>
        </Stack>

        {deckRows.length > 0 && (
          <Stack spacing={4}>
            <Text fw={600} size="sm">
              Deck assessment
            </Text>
            <div className={styles.detailGrid}>
              {deckRows.map((row) => (
                <div key={row.label} className={styles.detailRow}>
                  <span className={styles.detailLabel}>{row.label}</span>
                  <span>{row.value}</span>
                </div>
              ))}
            </div>
          </Stack>
        )}

        {dppRows.length > 0 && (
          <Stack spacing={4}>
            <Text fw={600} size="sm">
              DPP identification
            </Text>
            <div className={styles.detailGrid}>
              {dppRows.map((row) => (
                <div key={row.label} className={styles.detailRow}>
                  <span className={styles.detailLabel}>{row.label}</span>
                  <span>{row.value}</span>
                </div>
              ))}
            </div>
          </Stack>
        )}

        {chapterTagSyncRows.length > 0 && (
          <Stack spacing={4}>
            <Text fw={600} size="sm">
              Chapter tag sync
            </Text>
            <div className={styles.detailGrid}>
              {chapterTagSyncRows.map((row) => (
                <div key={row.label} className={styles.detailRow}>
                  <span className={styles.detailLabel}>{row.label}</span>
                  <span>{row.value}</span>
                </div>
              ))}
            </div>
          </Stack>
        )}

        {scheduleSyncRows.length > 0 && (
          <Stack spacing={4}>
            <Text fw={600} size="sm">
              Schedule sync
            </Text>
            <div className={styles.detailGrid}>
              {scheduleSyncRows.map((row) => (
                <div key={row.label} className={styles.detailRow}>
                  <span className={styles.detailLabel}>{row.label}</span>
                  <span>{row.value}</span>
                </div>
              ))}
            </div>
          </Stack>
        )}

        {(updatedPdfUrl || originalPdfUrl) && (
          <Stack spacing="xs">
            <Text fw={600} size="sm">
              PDF links
            </Text>
            <div className={styles.linkRow}>
              {updatedPdfUrl && (
                <Anchor href={updatedPdfUrl} target="_blank" rel="noreferrer">
                  <Button size="xs" variant="filled">
                    Preview / download updated PDF
                  </Button>
                </Anchor>
              )}
              {originalPdfUrl && (
                <Anchor href={originalPdfUrl} target="_blank" rel="noreferrer">
                  <Button size="xs" variant="outline">
                    Preview / download original PDF
                  </Button>
                </Anchor>
              )}
            </div>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
};

export default DeckAssessmentModal;
